import { GrowthBook } from "@growthbook/growthbook-react";

import Sentry from "@similarweb/sentry-logger";

import type { ISwSettings } from "app/@types/ISwSettings";
import { TEAMS } from "app/constants/teamName";
import type { IGrowthbookService, Tests, TestValues } from "app/services/trackingServices/types";

import { getClientKey, getUserType, mixpanelTracking, pageViewTrackingUtility } from "./GrowthBookServiceUtil";

export enum FeatureFlags {}

export enum TestsNames {
    ACTIVATION_HOME_PAGE_EMPTY_STATE = "hwx-2058-homepage-empty-state",
    TOTAL_VISITS_HOOK = "lynx-234",
    TRIAL_TABLE_HOOK = "si-7405-limit-number-of-items",
    TRIAL_BANNER = "bzz-3552-trial-banner",
    SI_SIDEBAR_ICON = "si-7723-sidebar-icon",
    ADD_ON_TOASTER = "lnx-348",
    PAYMENT_PERIOD = "hwx-2664",
    AEO_CAMPAIGN = "lnx-413",
    AFTER_TRIAL_DS_HOME_SIDEBAR = "hwx-2712",
    ONBOARDING_AGENT = "hwx-2745",
    SEASONAL_DISCOUNTS = "lnx-649",
    ANNUAL_PROMOTION = "lnx-635",
    UPGRADE_FROM_HOOKS = "lnx-509",
    REACTIVATION_PACKAGES_MODAL_V2 = "hwx-3277",
    TOUCH_HOOKS_DUMMY_PAGE = "lnx-702",
    NEW_PURCHASE_WIZARD = "lnx-668",
    AI_REPORT_CTA = "lnx-699",
}

const growthbookInstance = new GrowthBook({
    apiHost: "https://cdn.growthbook.io",
    clientKey: getClientKey(),
    enableDevMode: true,
});

let activeTestsResults: Partial<Tests> | null = null;

const pageViewTracking = (): { ab_test_name: string; ab_test_value: string } | null => {
    if (!activeTestsResults) {
        return null;
    }

    return pageViewTrackingUtility(activeTestsResults);
};

const evaluateAndTrackTests = (): void => {
    const testsResultsForTracking: Partial<Tests> = {};

    Object.values(TestsNames).forEach((testName) => {
        const evalFeature = growthbookInstance.evalFeature(testName);
        if (evalFeature.source === "experiment") {
            testsResultsForTracking[testName] = evalFeature.experimentResult.name as TestValues;
        }
    });

    activeTestsResults = testsResultsForTracking;
    mixpanelTracking(testsResultsForTracking);
};

let growthbookStreamingStarted = false;

const enableGrowthbookStreaming = (): void => {
    if (growthbookStreamingStarted) {
        return;
    }
    growthbookStreamingStarted = true;

    void growthbookInstance.init({ streaming: true }).catch((error) => {
        Sentry.errorTracker({
            message: "Error enabling Growthbook streaming",
            e: error,
            level: "warning",
            params: {
                tags: { team: TEAMS.GROWTH },
            },
        });
    });
};

const scheduleGrowthbookStreaming = (): void => {
    if (document.readyState === "complete") {
        enableGrowthbookStreaming();
        return;
    }

    window.addEventListener("load", enableGrowthbookStreaming, { once: true });
};

export const initGrowthbook = async (swSettings: ISwSettings) => {
    const {
        user: { id, userCountryByIp, createDate },
    } = swSettings;

    try {
        /* When adding new attributes, make sure to update the attributes in the Growthbook dashboard */
        await growthbookInstance.setAttributes({
            id,
            userCountryByIp,
            createDate,
            ...getUserType(swSettings),
        });

        await growthbookInstance.init({
            // Fetch features during bootstrap; open the SSE stream only after page load
            // to avoid Firefox aborting /sub/ connections while the document is still loading.
            streaming: false,
        });

        evaluateAndTrackTests();
        scheduleGrowthbookStreaming();
    } catch (error) {
        Sentry.errorTracker({
            message: "Error initializing Growthbook",
            e: error,
            level: "error",
            params: {
                tags: { team: TEAMS.GROWTH },
            },
        });
    }
};

export const GrowthbookService: IGrowthbookService = {
    pageViewTracking,
    growthbookInstance,
};
